import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { SearchFlowLabels, SearchFlowStep, SearchFlowTemplate } from 'voyager';
import {
  buildSearchHandoffQuery,
  getCategoryListRoute,
  isSingleDateCategory,
  isSpaCategory,
  isTablesCategory,
  showsChildrenCounter,
} from './categoryRouting';
import { SearchFlowProps } from './SearchFlow.types';

type HomepageEntity = {
  heroBackgroundImage?: { url?: string };
};

const DEFAULT_ADULTS = 2;
const DEFAULT_CHILDREN = 0;

function readQueryString(value: string | string[] | undefined): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readPositiveInt(value: string | string[] | undefined, fallback: number): number {
  const raw = readQueryString(value);
  if (!raw || !/^\d+$/.test(raw)) return fallback;
  return Number(raw);
}

type QueryOverrides = Partial<{
  step: SearchFlowStep;
  from: string | null;
  to: string | null;
  adults: number;
  children: number;
}>;

export const SearchFlow: FC<SearchFlowProps> = ({ property }) => {
  const router = useRouter();
  const { t } = useTranslations();

  const homepageId = property.homepage?.id;
  const homepage = useSelector((state) =>
    homepageId ? (entitySelectors.getHomepage(state, homepageId) as HomepageEntity) : null
  );
  const heroImageUrl =
    homepage?.heroBackgroundImage?.url || property.homepage?.heroBackgroundImage?.url || null;

  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);

  const category = readQueryString(router.query.category) ?? '';
  const step: SearchFlowStep = readQueryString(router.query.step) === 'who' ? 'who' : 'when';
  const checkIn = readQueryString(router.query.from) ?? null;
  const checkOut = readQueryString(router.query.to) ?? null;
  const defaultAdults = isSpaCategory(category)
    ? journeyParams.spaAdults
    : isTablesCategory(category)
    ? journeyParams.tableAdults
    : journeyParams.adults;
  const adults = readPositiveInt(router.query.adults, defaultAdults || DEFAULT_ADULTS);
  const showChildren = showsChildrenCounter(category);
  const childCount = showChildren
    ? readPositiveInt(router.query.children, journeyParams.children || DEFAULT_CHILDREN)
    : 0;

  const singleDate = isSingleDateCategory(category);

  const buildQuery = useCallback(
    (overrides: QueryOverrides): Record<string, string | number> => {
      const merged = {
        step,
        from: checkIn,
        to: checkOut,
        adults,
        children: childCount,
        ...overrides,
      };
      const out: Record<string, string | number> = { category, step: merged.step };
      if (merged.from) {
        out.from = merged.from;
        const to = singleDate ? merged.from : merged.to;
        if (to) out.to = to;
      }
      out.adults = merged.adults;
      if (showChildren) {
        out.children = merged.children;
      }
      return out;
    },
    [category, step, checkIn, checkOut, adults, childCount, singleDate, showChildren]
  );

  const updateUrl = useCallback(
    (overrides: QueryOverrides, method: 'push' | 'replace' = 'replace') => {
      router[method]({ pathname: Routes.SEARCH, query: buildQuery(overrides) }, undefined, {
        shallow: true,
      });
    },
    [router, buildQuery]
  );

  const handleRangeSelect = useCallback(
    (from: string, to: string) => updateUrl({ from, to }),
    [updateUrl]
  );

  const handleAdultsChange = useCallback(
    (value: number) => updateUrl({ adults: value }),
    [updateUrl]
  );

  const handleChildrenChange = useCallback(
    (value: number) => updateUrl({ children: value }),
    [updateUrl]
  );

  const goToStep = useCallback(
    (next: SearchFlowStep, { keepDates = true }: { keepDates?: boolean } = {}) => {
      updateUrl(keepDates ? { step: next } : { step: next, from: null, to: null }, 'push');
    },
    [updateUrl]
  );

  const handleSelectStep = useCallback((next: SearchFlowStep) => goToStep(next), [goToStep]);
  const handleSkip = useCallback(() => goToStep('who', { keepDates: false }), [goToStep]);
  const handleNext = useCallback(() => goToStep('who'), [goToStep]);

  const handleBack = useCallback(() => {
    if (step === 'who') {
      goToStep('when');
      return;
    }
    router.push({ pathname: Routes.HOME });
  }, [step, goToStep, router]);

  const handleSearch = useCallback(() => {
    const route = getCategoryListRoute(category);
    if (!route) {
      router.push({ pathname: Routes.HOME });
      return;
    }
    const query = buildSearchHandoffQuery(category, {
      from: checkIn,
      to: checkOut,
      adults,
      children: childCount,
    });
    router.push({ pathname: route, query });
  }, [router, category, adults, childCount, checkIn, checkOut]);

  const labels = useMemo<SearchFlowLabels>(
    () => ({
      whenTitle: t('search.whenAreYouVisiting'),
      whoTitle: t('search.whosComing'),
      addDates: t('search.addDates'),
      addGuests: t('search.addGuests'),
      skip: t('common.skip'),
      next: t('common.next'),
      search: t('common.search'),
      back: t('common.back'),
      guests: (total: number) => `${total} ${total === 1 ? t('common.guest') : t('common.guests')}`,
      guestPicker: {
        adults: t('common.adults'),
        children: t('common.children'),
        decreaseAdults: t('search.decreaseAdults'),
        increaseAdults: t('search.increaseAdults'),
        decreaseChildren: t('search.decreaseChildren'),
        increaseChildren: t('search.increaseChildren'),
      },
    }),
    [t]
  );

  return (
    <SearchFlowTemplate
      step={step}
      heroImageUrl={heroImageUrl}
      checkIn={checkIn}
      checkOut={checkOut}
      singleDateMode={singleDate}
      onRangeSelect={handleRangeSelect}
      adults={adults}
      childCount={childCount}
      onAdultsChange={handleAdultsChange}
      onChildrenChange={handleChildrenChange}
      showChildren={showChildren}
      onBack={handleBack}
      onSelectStep={handleSelectStep}
      onSkip={handleSkip}
      onNext={handleNext}
      onSearch={handleSearch}
      labels={labels}
    />
  );
};
